import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkSession } from '../../utils/sessionManager';
import { siteKey, saveActivationRequest, getAllActivationRequests } from '../../utils/sharedStorage';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

/* -------------------------------------------------------------------------- */
/*  Configuration & Types                                                      */
/* -------------------------------------------------------------------------- */
const COUNTRY_CONFIG: Record<string, { currency: string; symbol: string; amount: number }> = {
  "Côte d'Ivoire": { currency: 'XOF', symbol: 'FCFA', amount: 3500 },
  Sénégal: { currency: 'XOF', symbol: 'FCFA', amount: 3500 },
  Bénin: { currency: 'XOF', symbol: 'FCFA', amount: 3500 },
  Mali: { currency: 'XOF', symbol: 'FCFA', amount: 3500 },
  Togo: { currency: 'XOF', symbol: 'FCFA', amount: 3500 },
  'Burkina Faso': { currency: 'XOF', symbol: 'FCFA', amount: 3500 },
  'Congo Kinshasa': { currency: 'CDF', symbol: 'FC', amount: 3500 },
  Gabon: { currency: 'XAF', symbol: 'FCFA', amount: 3500 },
  Cameroun: { currency: 'XAF', symbol: 'FCFA', amount: 3500 },
  'Congo Brazzaville': { currency: 'XAF', symbol: 'FCFA', amount: 3500 },
  Ghana: { currency: 'GHS', symbol: 'GH₵', amount: 3500 },
};

interface PaymentConfig {
  bkapay: { enabled: boolean; url: string; label: string };
  usdt: { enabled: boolean; address: string; label: string };
  moneyfusion: { enabled: boolean; url: string; label: string };
  soleaspay: { enabled: boolean; url: string; label: string };
  ghana: { enabled: boolean; number: string; label: string };
}

const DEFAULT_PAYMENT_CONFIG: PaymentConfig = {
  bkapay: { enabled: true, url: 'https://bkapay.com/pay/b7213d6c', label: 'BKaPay' },
  usdt: { enabled: true, address: '0xCF9EFE5aC5e5fbebD7cd41Ff0534Bec12016FCC5', label: 'USDT BEP20' },
  moneyfusion: { enabled: true, url: 'https://my.moneyfusion.net/69943d3a720a7f15e8b1fff6', label: 'Money Fusion' },
  soleaspay: { enabled: true, url: 'https://ashtechpay.top/pay/Successlinkagencia', label: 'Soleaspay' },
  ghana: { enabled: true, number: '', label: 'Mobile Money Ghana' },
};

type PaymentMethod = 'bkapay' | 'moneyfusion' | 'usdt' | 'soleaspay' | 'ghana';
type Step = 'choose' | 'pay' | 'proof' | 'pending' | 'activated';

interface ActivationRequest {
  id: string;
  userPhone: string;
  userName: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  proof: string;
  paymentPhone?: string;
  screenshotProof?: string;
  status: 'pending' | 'validated' | 'approved' | 'rejected';
  rejectionReason?: string;
  date: string;
  country: string;
}

/* -------------------------------------------------------------------------- */
/*  LocalStorage helpers                                                       */
/* -------------------------------------------------------------------------- */
const SESSION_KEY = 'payment_session_persistent';

interface SessionData {
  paymentMethod: PaymentMethod;
  step: Step;
  currentRequestId?: string;
  proofText?: string;
  paymentPhone?: string;
  timestamp: number;
}

function saveSession(data: SessionData) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(data));
}
function loadSession(): SessionData | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d.timestamp < Date.now() - 90 * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return d;
  } catch { return null; }
}
function clearSession() { localStorage.removeItem(SESSION_KEY); }

/* -------------------------------------------------------------------------- */
/*  Helpers localStorage uniquement (plus de Supabase)                        */
/* -------------------------------------------------------------------------- */
async function saveRequest(req: ActivationRequest) {
  // Utiliser la fonction saveActivationRequest qui écrit dans Supabase + localStorage
  await saveActivationRequest(req);
}

function activateLocalUser(phone: string) {
  const cu = JSON.parse(localStorage.getItem('currentUser') || '{}');
  cu.accountStatus = 'active'; cu.isActivated = true; cu.activated = true;
  localStorage.setItem('currentUser', JSON.stringify(cu));
  const key = siteKey(`user_${phone}`);
  const ud = JSON.parse(localStorage.getItem(key) || localStorage.getItem(`user_${phone}`) || '{}');
  ud.accountStatus = 'active'; ud.isActivated = true; ud.activated = true;
  localStorage.setItem(key, JSON.stringify(ud));
  localStorage.setItem(`user_${phone}`, JSON.stringify(ud));
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                             */
/* -------------------------------------------------------------------------- */
const Activation = () => {
  const navigate = useNavigate();

  const [userPhone, setUserPhone] = useState('');
  const [userName, setUserName] = useState('');
  const [country, setCountry] = useState('');
  const [symbol, setSymbol] = useState('FCFA');
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig>(DEFAULT_PAYMENT_CONFIG);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('bkapay');
  const [step, setStep] = useState<Step>('choose');
  const [currentRequestId, setCurrentRequestId] = useState('');
  const [proofText, setProofText] = useState('');
  const [proofError, setProofError] = useState('');
  const [paymentPhone, setPaymentPhone] = useState('');
  const [screenshotPreview, setScreenshotPreview] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [copiedText, setCopiedText] = useState('');
  const [bkapayAutoActivation] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [bkapayTransactionId] = useState('');

  /* ── Init ── */
  useEffect(() => {
    const session = checkSession();
    if (!session.isValid) { navigate('/connexion'); return; }
    const raw = localStorage.getItem('currentUser');
    if (!raw) { navigate('/connexion'); return; }
    const userData = JSON.parse(raw);
    if (userData.isActivated || userData.activated) { navigate('/tableau-de-bord'); return; }

    setUserPhone(userData.phone || '');
    setUserName(userData.name || '');
    if (userData.country) {
      setCountry(userData.country);
      setSymbol(COUNTRY_CONFIG[userData.country]?.symbol || 'FCFA');
    }

    // Admin config override
    const adminCfg = localStorage.getItem('adminPaymentConfig');
    if (adminCfg) { try { setPaymentConfig(JSON.parse(adminCfg)); } catch { /* ignore */ } }

    // Restore session
    const saved = loadSession();
    if (saved && saved.step && saved.step !== 'choose') {
      setPaymentMethod(saved.paymentMethod);
      setProofText(saved.proofText || '');
      setPaymentPhone(saved.paymentPhone || '');
      setCurrentRequestId(saved.currentRequestId || '');
      if (saved.step === 'pending') {
        checkPendingLocal(userData.phone).then(hasPending => {
          setStep(hasPending ? 'pending' : 'choose');
        });
      } else {
        setStep(saved.step);
      }
    } else {
      checkPendingLocal(userData.phone).then(hasPending => {
        if (hasPending) setStep('pending');
      });
    }
  }, [navigate]);

  const checkPendingLocal = async (phone: string): Promise<boolean> => {
    if (!phone) return false;
    try {
      const all: ActivationRequest[] = JSON.parse(
        localStorage.getItem(siteKey('activationRequests')) || localStorage.getItem('activationRequests') || '[]'
      );
      const pending = all.find(r => r.userPhone === phone && r.status === 'pending');
      if (pending) { setCurrentRequestId(pending.id); return true; }
    } catch { /* ignore */ }
    return false;
  };

  /* ── Abonnement Supabase Realtime pour détecter la validation instantanée ── */
  useEffect(() => {
    if (step !== 'pending' || !currentRequestId || !isSupabaseConfigured) return;

    // Abonnement Realtime sur activation_requests
    const channel = supabase
      .channel(`activation_${currentRequestId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'activation_requests',
          filter: `id=eq.${currentRequestId}`,
        },
        (payload) => {
          console.log('🔄 Changement détecté sur la demande d\'activation:', payload);
          const newData = payload.new as any;

          if (newData.status === 'validated' || newData.status === 'approved') {
            // Activation validée
            activateLocalUser(userPhone);
            clearSession();
            setStep('activated');

            // Notifier le tableau de bord
            try {
              const ch = new BroadcastChannel('account_activation');
              ch.postMessage({ phone: userPhone, activated: true });
              ch.close();
            } catch { /* ignore */ }
          } else if (newData.status === 'rejected') {
            // Paiement rejeté
            setRejectionReason(newData.rejectionReason || 'Paiement refusé');
            const cu = JSON.parse(localStorage.getItem('currentUser') || '{}');
            cu.accountStatus = 'rejected';
            localStorage.setItem('currentUser', JSON.stringify(cu));
            clearSession();
            setStep('choose');
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [step, currentRequestId, userPhone]);

  /* ── Polling localStorage uniquement (fallback) ── */
  useEffect(() => {
    if (step !== 'pending') return;
    const interval = setInterval(() => {
      const all: ActivationRequest[] = JSON.parse(
        localStorage.getItem(siteKey('activationRequests')) || localStorage.getItem('activationRequests') || '[]'
      );
      const req = all.find(r => r.id === currentRequestId);
      if (!req) return;
      if (req.status === 'validated' || req.status === 'approved') {
        activateLocalUser(userPhone); clearSession(); setStep('activated'); clearInterval(interval);
      } else if (req.status === 'rejected') {
        setRejectionReason(req.rejectionReason || 'Paiement refusé');
        const cu = JSON.parse(localStorage.getItem('currentUser') || '{}');
        cu.accountStatus = 'rejected'; localStorage.setItem('currentUser', JSON.stringify(cu));
        clearSession(); setStep('choose'); clearInterval(interval);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [step, currentRequestId, userPhone]);

  const copyToClipboard = async (text: string, type: string) => {
    try { await navigator.clipboard.writeText(text); setCopiedText(type); setTimeout(() => setCopiedText(''), 2000); } catch { /* ignore */ }
  };

  const handleChooseAndPay = (method: PaymentMethod) => {
    setPaymentMethod(method);
    // Pour Money Fusion, ouvrir directement le lien et passer à l'étape proof
    if (method === 'moneyfusion') {
      const url = paymentConfig.moneyfusion.url || 'https://my.moneyfusion.net/69943d3a720a7f15e8b1fff6';
      window.open(url, '_blank');
      const requestId = crypto.randomUUID();
      const newReq: ActivationRequest = {
        id: requestId, userPhone, userName,
        amount: COUNTRY_CONFIG[country]?.amount || 3500,
        currency: COUNTRY_CONFIG[country]?.currency || 'XOF',
        paymentMethod: method, proof: '', status: 'pending',
        date: new Date().toISOString(), country,
      };
      saveRequest(newReq);
      setCurrentRequestId(requestId);
      setStep('proof');
      saveSession({ paymentMethod: method, step: 'proof', currentRequestId: requestId, timestamp: Date.now() });
      return;
    }
    setStep('pay');
    saveSession({ paymentMethod: method, step: 'pay', timestamp: Date.now() });
  };

  const handleOpenPaymentLink = () => {
    const urls: Record<string, string> = {
      bkapay: paymentConfig.bkapay.url || 'https://bkapay.com/pay/b7213d6c',
      moneyfusion: paymentConfig.moneyfusion.url || 'https://my.moneyfusion.net/69943d3a720a7f15e8b1fff6',
      soleaspay: paymentConfig.soleaspay.url || '',
    };
    const url = urls[paymentMethod];
    if (url) window.open(url, '_blank');
  };

  const handleIHavePaid = async () => {
    const requestId = currentRequestId || crypto.randomUUID();
    if (!currentRequestId) {
      const newReq: ActivationRequest = {
        id: requestId, userPhone, userName,
        amount: COUNTRY_CONFIG[country]?.amount || 3500,
        currency: COUNTRY_CONFIG[country]?.currency || 'XOF',
        paymentMethod, proof: '', status: 'pending',
        date: new Date().toISOString(), country,
      };
      await saveRequest(newReq);
      setCurrentRequestId(requestId);
    }
    setStep('proof');
    saveSession({ paymentMethod, step: 'proof', currentRequestId: requestId, timestamp: Date.now() });
  };

  const handleSubmitProof = async () => {
    if (!proofText.trim()) { setProofError('La référence de transaction est obligatoire.'); return; }
    setIsSending(true);
    const requestId = currentRequestId || crypto.randomUUID();
    const updatedReq: ActivationRequest = {
      id: requestId, userPhone, userName,
      amount: COUNTRY_CONFIG[country]?.amount || 3500,
      currency: COUNTRY_CONFIG[country]?.currency || 'XOF',
      paymentMethod, proof: proofText,
      paymentPhone, screenshotProof: screenshotPreview,
      status: 'pending', date: new Date().toISOString(), country,
    };
    await saveRequest(updatedReq);
    setCurrentRequestId(requestId);

    // ── Notifier le panel admin instantanément ──
    try {
      const ch = new BroadcastChannel('new_payment_submitted');
      ch.postMessage({ type: 'NEW_PAYMENT', userPhone, userName, paymentMethod, requestId });
      ch.close();
    } catch { /* ignore */ }

    setStep('pending');
    setIsSending(false);
    saveSession({ paymentMethod, step: 'pending', currentRequestId: requestId, proofText, paymentPhone, timestamp: Date.now() });
  };

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('La capture ne doit pas dépasser 5 Mo.'); return; }
    const reader = new FileReader();
    reader.onload = () => setScreenshotPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  /* ── Step indicator ── */
  const StepBar = ({ current }: { current: number }) => (
    <div className="flex items-center justify-center gap-1 mt-4">
      {['Choisir', 'Payer', 'Preuve', 'Attente'].map((label, i) => {
        const num = i + 1;
        const done = num < current;
        const active = num === current;
        return (
          <div key={label} className="flex items-center gap-1">
            <div className="flex flex-col items-center gap-0.5">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${done ? 'bg-white text-emerald-700' : active ? 'bg-white/30 text-white border-2 border-white' : 'bg-white/10 text-white/40'}`}>
                {done ? <i className="ri-check-line text-xs"></i> : num}
              </div>
              <span className={`text-xs ${active ? 'text-white font-semibold' : 'text-white/40'}`}>{label}</span>
            </div>
            {i < 3 && <div className={`w-5 h-0.5 mb-4 ${done ? 'bg-white' : 'bg-white/20'}`}></div>}
          </div>
        );
      })}
    </div>
  );

  const Header = ({ stepNum }: { stepNum: number }) => (
    <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-8 py-6 text-center">
      <img src="https://static.readdy.ai/image/82792080bea9235a3a5c58a151cce2b4/fff4bb4e89a9e983dddf5fffb9dae068.png" alt="SUCCESSLINK AGENCY" className="w-14 h-14 object-contain mx-auto mb-2 rounded-full bg-white p-1" />
      <h1 className="text-lg font-bold text-white">SUCCESSLINK AGENCY</h1>
      <p className="text-emerald-100 text-xs mt-0.5">Activation de compte</p>
      <StepBar current={stepNum} />
    </div>
  );

  /* ================================================================ */
  /*  STEP 1 — Choisir la méthode de paiement                         */
  /* ================================================================ */
  if (step === 'choose') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center py-10 px-4">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <Header stepNum={1} />
            <div className="px-6 py-6 space-y-5">

              {/* Rejection banner */}
              {rejectionReason && (
                <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                      <i className="ri-close-circle-fill text-red-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-red-700 mb-1">❌ Paiement précédent refusé</p>
                      <p className="text-xs text-red-600 italic">&quot;{rejectionReason}&quot;</p>
                      <p className="text-xs text-red-500 mt-1">Veuillez effectuer un nouveau paiement valide.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Montant */}
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-5 text-center text-white">
                <p className="text-sm font-medium opacity-80 mb-1">Frais d&apos;activation uniques</p>
                <p className="text-5xl font-extrabold">3 500</p>
                <p className="text-2xl font-bold opacity-90">{symbol}</p>
                {country && <p className="text-xs opacity-70 mt-1">{country}</p>}
              </div>

              {/* Instructions */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                <p className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-2">
                  <i className="ri-information-line text-amber-600"></i>
                  Comment ça marche ?
                </p>
                <ol className="space-y-1.5">
                  {[
                    'Choisissez votre méthode de paiement ci-dessous',
                    'Cliquez sur le bouton pour ouvrir la page de paiement',
                    'Effectuez le paiement de 3 500 ' + symbol,
                    'Revenez ici et soumettez votre référence de transaction',
                    "L'admin valide → votre compte s'ouvre automatiquement ✅",
                  ].map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-amber-800">
                      <span className="w-5 h-5 flex items-center justify-center bg-amber-200 text-amber-700 rounded-full font-bold text-xs flex-shrink-0 mt-0.5">{i + 1}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Méthodes de paiement */}
              <div>
                <p className="text-sm font-bold text-gray-700 mb-3">Choisissez votre méthode de paiement :</p>
                <div className="space-y-3">

                  {/* BKaPay */}
                  {paymentConfig.bkapay.enabled && (
                    <button
                      onClick={() => handleChooseAndPay('bkapay')}
                      className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-2xl p-4 flex items-center gap-4 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                    >
                      <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                        <i className="ri-bank-card-2-line text-3xl text-white"></i>
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-extrabold text-lg">BKaPay</p>
                        <p className="text-xs text-emerald-100">Mobile Money · Carte bancaire</p>
                        <p className="text-xs text-white/70 mt-0.5 font-mono">bkapay.com/pay/b7213d6c</p>
                      </div>
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full group-hover:bg-white/30 transition flex-shrink-0">
                        <i className="ri-arrow-right-line text-xl text-white"></i>
                      </div>
                    </button>
                  )}

                  {/* Money Fusion */}
                  {paymentConfig.moneyfusion.enabled && (
                    <button
                      onClick={() => handleChooseAndPay('moneyfusion')}
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-2xl p-4 flex items-center gap-4 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                    >
                      <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                        <i className="ri-exchange-dollar-line text-3xl text-white"></i>
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-extrabold text-lg">Money Fusion</p>
                        <p className="text-xs text-orange-100">Paiement mobile sécurisé · Rapide</p>
                        <p className="text-xs text-white/70 mt-0.5 font-mono">my.moneyfusion.net</p>
                      </div>
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full group-hover:bg-white/30 transition flex-shrink-0">
                        <i className="ri-external-link-line text-xl text-white"></i>
                      </div>
                    </button>
                  )}

                  {/* USDT */}
                  {paymentConfig.usdt.enabled && (
                    <button
                      onClick={() => handleChooseAndPay('usdt')}
                      className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white rounded-2xl p-4 flex items-center gap-4 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                    >
                      <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                        <i className="ri-bit-coin-line text-3xl text-white"></i>
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-extrabold text-lg">USDT BEP20</p>
                        <p className="text-xs text-yellow-100">Crypto · Binance Smart Chain</p>
                      </div>
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full group-hover:bg-white/30 transition flex-shrink-0">
                        <i className="ri-arrow-right-line text-xl text-white"></i>
                      </div>
                    </button>
                  )}

                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ================================================================ */
  /*  STEP 2 — Payer (lien ouvert)                                     */
  /* ================================================================ */
  if (step === 'pay') {
    const methodInfo: Record<PaymentMethod, { label: string; color: string; icon: string; url?: string; address?: string }> = {
      bkapay: { label: 'BKaPay', color: 'emerald', icon: 'ri-bank-card-2-line', url: paymentConfig.bkapay.url || 'https://bkapay.com/pay/b7213d6c' },
      moneyfusion: { label: 'Money Fusion', color: 'orange', icon: 'ri-exchange-dollar-line', url: paymentConfig.moneyfusion.url || 'https://my.moneyfusion.net/69943d3a720a7f15e8b1fff6' },
      usdt: { label: 'USDT BEP20', color: 'yellow', icon: 'ri-bit-coin-line', address: paymentConfig.usdt.address },
      soleaspay: { label: 'Soleaspay', color: 'emerald', icon: 'ri-redirect-line', url: paymentConfig.soleaspay.url },
      ghana: { label: 'Mobile Money Ghana', color: 'emerald', icon: 'ri-wallet-line' },
    };
    const info = methodInfo[paymentMethod];
    const colorMap: Record<string, string> = {
      emerald: 'from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700',
      orange: 'from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700',
      yellow: 'from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700',
    };
    const btnColor = colorMap[info.color] || colorMap.emerald;

    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center py-10 px-4">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <Header stepNum={2} />
            <div className="px-6 py-6 space-y-5">

              {/* Méthode choisie */}
              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
                  <i className={`${info.icon} text-emerald-600 text-xl`}></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Méthode choisie</p>
                  <p className="font-bold text-gray-800">{info.label}</p>
                </div>
                <button onClick={() => { setStep('choose'); clearSession(); }} className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold underline cursor-pointer whitespace-nowrap">Changer</button>
              </div>

              {/* Montant à payer */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-5 text-center text-white">
                <p className="text-sm font-medium opacity-90 mb-1">Montant à payer</p>
                <p className="text-5xl font-extrabold">3 500</p>
                <p className="text-2xl font-bold opacity-90">{symbol}</p>
              </div>

              {/* Lien de paiement ou adresse USDT */}
              {info.url ? (
                <div className="space-y-3">
                  <p className="text-sm font-bold text-gray-700 text-center">
                    👇 Cliquez sur le bouton ci-dessous pour payer
                  </p>
                  <a
                    href={info.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full bg-gradient-to-r ${btnColor} text-white font-extrabold py-5 rounded-2xl text-base transition shadow-xl hover:shadow-2xl cursor-pointer flex items-center justify-center gap-3`}
                  >
                    <i className={`${info.icon} text-2xl`}></i>
                    Payer 3 500 {symbol} via {info.label}
                    <i className="ri-external-link-line text-lg opacity-80"></i>
                  </a>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-2">
                    <i className="ri-link text-gray-400 text-sm flex-shrink-0"></i>
                    <p className="text-xs text-gray-500 font-mono break-all flex-1">{info.url}</p>
                    <button onClick={() => copyToClipboard(info.url!, 'url')} className="flex-shrink-0 px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs rounded-lg cursor-pointer whitespace-nowrap">
                      {copiedText === 'url' ? '✓' : <i className="ri-file-copy-line"></i>}
                    </button>
                  </div>
                </div>
              ) : info.address ? (
                <div className="space-y-3">
                  <p className="text-sm font-bold text-gray-700">Adresse USDT BEP20 :</p>
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                    <p className="text-xs font-mono text-gray-700 break-all mb-3">{info.address}</p>
                    <button onClick={() => copyToClipboard(info.address!, 'usdt')} className="w-full py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                      <i className="ri-file-copy-line"></i>
                      {copiedText === 'usdt' ? '✓ Adresse copiée !' : 'Copier l\'adresse'}
                    </button>
                  </div>
                </div>
              ) : null}

              {/* Instruction après paiement */}
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 text-center">
                <p className="text-sm font-bold text-emerald-800 mb-1">
                  ✅ Après avoir payé, revenez ici
                </p>
                <p className="text-xs text-emerald-700">
                  Cliquez sur &quot;J&apos;ai payé&quot; pour soumettre votre preuve de paiement.
                </p>
              </div>

              {/* Boutons */}
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => { setStep('choose'); clearSession(); }} className="py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                  <i className="ri-arrow-left-line"></i>Retour
                </button>
                <button onClick={handleIHavePaid} className="py-3 px-4 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow">
                  <i className="ri-check-double-line"></i>J&apos;ai payé ✓
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ================================================================ */
  /*  STEP 3 — Soumettre la preuve                                     */
  /* ================================================================ */
  if (step === 'proof') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center py-10 px-4">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <Header stepNum={3} />
            <div className="px-6 py-6 space-y-5">

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-3">
                  <i className="ri-file-text-line text-3xl text-emerald-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800">Confirmez votre paiement</h3>
                <p className="text-sm text-gray-500 mt-1">Entrez la référence de votre transaction.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Numéro utilisé pour le paiement
                </label>
                <input
                  type="tel"
                  value={paymentPhone}
                  onChange={e => setPaymentPhone(e.target.value)}
                  placeholder="Ex: +225 07 00 00 00 00"
                  className="w-full px-4 py-3 border-2 border-gray-200 bg-gray-50 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Référence de transaction <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={proofText}
                  onChange={e => { setProofText(e.target.value); setProofError(''); }}
                  placeholder="Ex: REF-123456789"
                  className={`w-full px-4 py-3 border-2 rounded-xl text-sm font-mono focus:ring-2 focus:ring-emerald-500 outline-none transition ${proofError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:border-emerald-400'}`}
                />
                {proofError && (
                  <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i> {proofError}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Capture d&apos;écran (optionnel mais recommandé)
                </label>
                <input type="file" accept="image/*" onChange={handleScreenshotChange} className="hidden" id="screenshot-upload" />
                <label htmlFor="screenshot-upload" className={`w-full px-4 py-5 border-2 border-dashed rounded-xl text-center cursor-pointer transition block ${screenshotPreview ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'}`}>
                  {screenshotPreview ? (
                    <div className="space-y-2">
                      <img src={screenshotPreview} alt="Preview" className="max-h-36 mx-auto rounded-lg border-2 border-emerald-200 shadow" />
                      <p className="text-xs text-emerald-700 font-semibold">Capture ajoutée ✓</p>
                    </div>
                  ) : (
                    <div>
                      <i className="ri-image-add-line text-3xl text-gray-300 mb-2 block"></i>
                      <p className="text-sm text-gray-500">Ajouter une capture d&apos;écran</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG — max 5 MB</p>
                    </div>
                  )}
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setStep('pay')} disabled={isSending} className="py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                  <i className="ri-arrow-left-line"></i>Retour
                </button>
                <button onClick={handleSubmitProof} disabled={!proofText.trim() || isSending} className={`py-3 px-4 font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow ${proofText.trim() && !isSending ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                  {isSending ? <><i className="ri-loader-4-line animate-spin"></i>Envoi...</> : <><i className="ri-send-plane-line"></i>Envoyer</>}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ================================================================ */
  /*  STEP 4 — En attente de validation                                */
  /* ================================================================ */
  if (step === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center py-10 px-4">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <Header stepNum={4} />
            <div className="px-6 py-6 space-y-5 text-center">

              <div className="w-24 h-24 flex items-center justify-center bg-yellow-100 rounded-full mx-auto relative">
                <i className="ri-time-line text-5xl text-yellow-500"></i>
                <div className="absolute -bottom-1 -right-1 w-8 h-8 flex items-center justify-center bg-emerald-500 rounded-full">
                  <i className="ri-shield-check-line text-white text-sm"></i>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-800">✅ Preuve envoyée !</h3>
                <p className="text-sm text-gray-500 mt-1">En attente de validation par l&apos;administrateur.</p>
              </div>

              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 flex items-start gap-3 text-left">
                <i className="ri-refresh-line text-emerald-600 text-xl animate-spin mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="font-bold text-emerald-800 text-sm mb-1">🔄 Vérification automatique</p>
                  <p className="text-xs text-emerald-700">Dès que l&apos;admin valide, votre compte s&apos;ouvre automatiquement.</p>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-left space-y-3">
                {[
                  { icon: 'ri-checkbox-circle-line text-green-500', bg: 'bg-green-100', text: 'Paiement soumis et enregistré ✓' },
                  { icon: 'ri-eye-line text-yellow-500', bg: 'bg-yellow-100', text: "En attente de vérification par l'administrateur..." },
                  { icon: 'ri-lock-line text-gray-400', bg: 'bg-gray-100', text: 'Activation du compte (après validation)', dim: true },
                ].map((item, i) => (
                  <div key={i} className={`flex items-center gap-3 text-sm ${item.dim ? 'opacity-40' : ''}`}>
                    <div className={`w-8 h-8 flex items-center justify-center ${item.bg} rounded-full flex-shrink-0`}>
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <span className="text-gray-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>

              {/* Renvoyer la référence */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 text-left">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-question-line text-amber-600 text-lg"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-amber-800 mb-1">Vous avez perdu votre soumission ?</p>
                    <p className="text-xs text-amber-700 mb-3">Cliquez ci-dessous pour renvoyer votre référence.</p>
                    <button
                      onClick={() => { setProofText(''); setPaymentPhone(''); setScreenshotPreview(''); setProofError(''); setStep('proof'); }}
                      className="w-full py-2.5 px-4 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-send-plane-2-line"></i>Renvoyer ma référence
                    </button>
                  </div>
                </div>
              </div>

              <button onClick={() => navigate('/connexion')} className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-logout-box-line"></i>Revenir plus tard
              </button>

            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ================================================================ */
  /*  STEP — Compte activé                                             */
  /* ================================================================ */
  if (step === 'activated') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center py-10 px-4">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-8 text-center space-y-5">
          <div className="w-24 h-24 flex items-center justify-center bg-emerald-100 rounded-full mx-auto">
            <i className="ri-checkbox-circle-fill text-5xl text-emerald-500"></i>
          </div>
          <h3 className="text-2xl font-extrabold text-emerald-700">🎉 Compte activé !</h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">Votre paiement a été confirmé. Bienvenue dans SUCCESSLINK AGENCY !</p>
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 text-left space-y-2">
            {['Paiement vérifié et confirmé', 'Compte activé avec succès', 'Accès complet au tableau de bord'].map((txt, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <i className="ri-checkbox-circle-line text-emerald-500 text-lg"></i>
                <span className="text-gray-700 font-semibold">{txt}</span>
              </div>
            ))}
          </div>
          <button onClick={() => navigate('/tableau-de-bord')} className="w-full py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-bold rounded-xl text-base transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-lg">
            <i className="ri-dashboard-line text-xl"></i>Accéder à mon tableau de bord
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default Activation;
